import { useEffect, useState, useCallback } from "react";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
export type RuntimeConfig = {
  demo: boolean;
  clinicName: string;
  issuer?: string;
  clientId: string;
  sparkUrl?: string;
  fhirConfigured: boolean;
};
export type User = { id: string; name: string; roles: string[] };
export class ApiError extends Error {
  constructor(
    public status: number,
    public data: {
      message: string;
      issues?: { linkId?: string; message: string }[];
    },
  ) {
    super(data.message);
  }
}
export function token() {
  return sessionStorage.getItem("iron.staff-token");
}
export async function api<T>(
  path: string,
  options: RequestInit = {},
  surveyToken?: string,
): Promise<T> {
  const response = await fetch(`/api${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(surveyToken
        ? { Authorization: `Survey ${surveyToken}` }
        : token()
          ? { Authorization: `Bearer ${token()}` }
          : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = await response
      .json()
      .catch(() => ({ message: "The server could not be reached." }));
    throw new ApiError(response.status, body);
  }
  return response.json() as Promise<T>;
}
export const write = <T>(
  path: string,
  body: unknown,
  method = "POST",
  surveyToken?: string,
) => api<T>(path, { method, body: JSON.stringify(body) }, surveyToken);
export function useResource<T>(path: string) {
  const [resource, setResource] = useState<{
    path: string;
    data?: T;
    error: string;
    loading: boolean;
  }>({ path, error: "", loading: true });
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    const abort = new AbortController();
    setResource({ path, loading: true, error: "" });
    api<T>(path, { signal: abort.signal })
      .then((data) => {
        if (!abort.signal.aborted)
          setResource({ path, data, loading: false, error: "" });
      })
      .catch((error) => {
        if (!abort.signal.aborted)
          setResource({ path, loading: false, error: error.message });
      });
    return () => abort.abort();
  }, [path, tick]);
  return {
    data: resource.path === path ? resource.data : undefined,
    error: resource.path === path ? resource.error : "",
    loading: resource.path !== path || resource.loading,
    reload,
  };
}
export function oidc(config: RuntimeConfig) {
  return new UserManager({
    authority: config.issuer!,
    client_id: config.clientId,
    redirect_uri: `${location.origin}/auth/callback`,
    post_logout_redirect_uri: `${location.origin}/`,
    response_type: "code",
    scope: "openid profile email",
    userStore: new WebStorageStateStore({ store: sessionStorage }),
    stateStore: new WebStorageStateStore({ store: sessionStorage }),
  });
}
export function download(name: string, value: unknown) {
  const blob = new Blob(
    [typeof value === "string" ? value : JSON.stringify(value, null, 2)],
    {
      type: name.endsWith(".csv")
        ? "text/csv;charset=utf-8"
        : "application/fhir+json",
    },
  );
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export const date = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
